import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { GroupInvitePolicy, GroupRole, GroupSummaryDTO } from '@nexply/api-client';
import { Badge, Button, EmptyState, Input, Label, PageHeader, Sheet, Textarea, useToast } from '@nexply/ui';
import { Plus, UsersRound, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api.js';

export const ROLE_LABEL: Record<GroupRole, string> = {
  OWNER: 'Ersteller',
  ADMIN: 'Admin',
  MEMBER: 'Mitglied',
};

export const ROLE_TONE: Record<GroupRole, 'gold' | 'warn' | 'neutral'> = {
  OWNER: 'gold',
  ADMIN: 'warn',
  MEMBER: 'neutral',
};

export function GroupsPage() {
  const nav = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const q = useQuery({ queryKey: ['groups'], queryFn: () => api.groups.list() });
  const groups = q.data?.groups ?? [];

  return (
    <div className="space-y-6 max-w-4xl animate-slide-up">
      <PageHeader
        eyebrow="Netzwerk"
        title="Netzwerk-Gruppen"
        subtitle="Lade Leute ein und pflegt gemeinsam ein Netzwerk."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Neue Gruppe
          </Button>
        }
      />

      {q.isLoading && (
        <div className="space-y-2.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 rounded-2xl bg-raise animate-pulse" />
          ))}
        </div>
      )}
      {q.isError && <div className="text-sm text-danger">Die Gruppen konnten nicht geladen werden.</div>}

      {q.data && groups.length === 0 && (
        <EmptyState
          mascot="idle"
          title="Noch keine Gruppe"
          description="Wie eine WhatsApp-Gruppe für dein Netzwerk: Gründe eine Gruppe für dein Team, deinen Verein oder deinen Business-Club. Wer dazukommt, ist automatisch mit allen Mitgliedern vernetzt."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              Erste Gruppe gründen
            </Button>
          }
        />
      )}

      <div className="space-y-2.5">
        {groups.map((g) => (
          <GroupRow key={g.id} g={g} onOpen={() => nav({ to: '/groups/$groupId', params: { groupId: g.id } })} />
        ))}
      </div>

      <CreateGroupSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => nav({ to: '/groups/$groupId', params: { groupId: id } })}
      />
    </div>
  );
}

function GroupRow({ g, onOpen }: { g: GroupSummaryDTO; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-2xl border border-line bg-surface-2 p-4 hover:border-gold/25 hover:bg-surface-3 transition"
    >
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gold/10 text-gold">
          <UsersRound className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-text">{g.name}</span>
            <Badge tone={ROLE_TONE[g.myRole]}>{ROLE_LABEL[g.myRole]}</Badge>
          </div>
          {g.description && <p className="mt-0.5 text-xs text-text-dim line-clamp-1">{g.description}</p>}
          <div className="mt-1 flex items-center gap-3 text-[11px] text-text-muted">
            <span className="inline-flex items-center gap-1">
              <UsersRound className="h-3 w-3" />
              {g.memberCount} {g.memberCount === 1 ? 'Mitglied' : 'Mitglieder'}
            </span>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-text-muted" />
      </div>
    </button>
  );
}

export function InvitePolicyPicker({
  value,
  onChange,
}: {
  value: GroupInvitePolicy;
  onChange: (v: GroupInvitePolicy) => void;
}) {
  const options: { value: GroupInvitePolicy; title: string; sub: string }[] = [
    { value: 'ADMINS_ONLY', title: 'Nur Admins', sub: 'Nur du und deine Admins können Leute einladen.' },
    { value: 'ALL_MEMBERS', title: 'Alle Mitglieder', sub: 'Jedes Mitglied darf Links teilen und Leute einladen.' },
  ];
  return (
    <div role="radiogroup" className="grid gap-2 sm:grid-cols-2">
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={
              'flex items-start gap-3 rounded-xl border-2 p-3 text-left transition ' +
              (on ? 'border-gold bg-gold/[0.12] shadow-glow-sm' : 'border-line bg-surface-2 hover:border-line-strong')
            }
          >
            <span
              className={
                'mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ' +
                (on ? 'border-gold' : 'border-line-strong')
              }
            >
              {on && <span className="h-2.5 w-2.5 rounded-full bg-gold" />}
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-2">
                <span className={'text-sm font-medium ' + (on ? 'text-gold-light' : 'text-text')}>{o.title}</span>
                {on && <Badge tone="gold">Ausgewählt</Badge>}
              </span>
              <span className="mt-0.5 block text-xs text-text-muted">{o.sub}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function CreateGroupSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [policy, setPolicy] = useState<GroupInvitePolicy>('ADMINS_ONLY');

  const create = useMutation({
    mutationFn: () => api.groups.create({ name, description, invitePolicy: policy }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Gruppe angelegt');
      setName('');
      setDescription('');
      setPolicy('ADMINS_ONLY');
      onClose();
      onCreated(res.group.id);
    },
    onError: (e: Error) => toast.error(e.message || 'Die Gruppe konnte nicht angelegt werden.'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate();
  }

  return (
    <Sheet open={open} onClose={onClose} title="Neue Netzwerk-Gruppe" size="md">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="g-name">Name</Label>
          <Input
            id="g-name"
            value={name}
            maxLength={120}
            autoFocus
            placeholder="z. B. BNI Chapter Köln"
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="g-desc">Beschreibung (optional)</Label>
          <Textarea
            id="g-desc"
            value={description}
            rows={3}
            maxLength={1000}
            placeholder="Worum geht es in der Gruppe?"
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Wer darf Leute einladen?</Label>
          <InvitePolicyPicker value={policy} onChange={setPolicy} />
          <p className="text-xs text-text-muted">Das kannst du später in den Einstellungen der Gruppe ändern.</p>
        </div>
        <Button type="submit" fullWidth loading={create.isPending} disabled={!name.trim()}>
          Gruppe gründen
        </Button>
      </form>
    </Sheet>
  );
}
